/**
 * 项目光点。
 *
 * 三层结构（移植自原型 `makePin`）：
 *   光晕（可见性，ALERT 时呼吸脉冲） + 双环（有范围） + 核心菱形（规模 / 健康）
 *
 * ⚠️ 颜色 = 健康等级（颜色即信息），禁止硬编码。
 */

import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { levelColor, type ProjectHealthLevel } from './colors';
import type { MapProject } from './useMapData';

export interface ProjectDotProps {
  project: MapProject;
  x: number;
  y: number;
  onEnter: (project: MapProject, e: ReactMouseEvent) => void;
  onLeave: () => void;
  onClick: (project: MapProject) => void;
  /** 是否处于筛选命中态（未命中会被淡化） */
  dimmed?: boolean;
  /** ⭐ P6.54 是否搜索命中并被定位（加聚焦环） */
  focused?: boolean;
}

export default function ProjectDot({
  project,
  x,
  y,
  onEnter,
  onLeave,
  onClick,
  dimmed = false,
  focused = false,
}: ProjectDotProps) {
  const color = levelColor(project.level);
  const size = Math.max(3.8, Math.min(11, 5.5 + (project.taskCount - 6) * 0.16));

  const stopPan = (e: ReactPointerEvent) => e.stopPropagation();

  return (
    <g
      className={`pin ${project.level as ProjectHealthLevel}${dimmed ? ' dim' : ''}${
        focused ? ' focus' : ''
      }`}
      transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`}
      style={{ cursor: 'pointer' }}
      onPointerDown={stopPan}
      onMouseEnter={(e) => onEnter(project, e)}
      onMouseLeave={onLeave}
      onClick={(e) => {
        e.stopPropagation();
        onClick(project);
      }}
      role="button"
      tabIndex={0}
      aria-label={`${project.name}（${project.provinceName}）`}
    >
      {focused && <circle className="focus-ring" r={size * 4} style={{ stroke: color }} />}
      <circle className="halo" r={size * 3.1} style={{ fill: color }} />
      <circle className="ring-out" r={size * 1.85} style={{ stroke: color }} />
      <circle className="ring-in" r={size * 1.28} style={{ stroke: color }} />
      <g transform="rotate(45)">
        <rect
          className="core"
          x={-size * 0.43}
          y={-size * 0.43}
          width={size * 0.86}
          height={size * 0.86}
          rx={0.5}
          style={{ fill: color }}
        />
      </g>
    </g>
  );
}
